import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import history from 'common/history';
import { fetchAskAiSseChatMessages } from 'features/case/redux/fetchAskAiSseChatMessages';

export function useChatHydration(params: {
  userId: any;
  caseId: any;
  chatIdFromUrl: string | null;
  isFileOpen?: boolean;
  fileSidebarAskAI?: boolean;
  skipHydration?: boolean;
}) {
  const { userId, caseId, chatIdFromUrl, isFileOpen, fileSidebarAskAI, skipHydration } = params;
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const didFetchChatMessagesRef = useRef<string | null>(null);

  // Get current chat ID from Redux to check if we already have the data
  const askAiSseChatId = useSelector((state: any) => state.case?.askAiSseChatId);

  // Hydrate chat history ONLY when the chatId is present in the URL (open existing chat / refresh).
  // Important: during an active SSE request we will receive chat_id via metadata; we must NOT
  // trigger hydration then, otherwise the fetch reducer will replace `askAiSseHistory` mid-stream.
  // Guarded for React 18 StrictMode (effects can run twice in dev).
  useEffect(() => {
    if (!userId || !chatIdFromUrl) return;
    if (skipHydration) {
      didFetchChatMessagesRef.current = String(chatIdFromUrl);
      return;
    }

    // If we are just opening a file and the chat ID in Redux matches the URL,
    // we assume the chat data is already loaded (e.g. from SSE stream) and we
    // should NOT re-fetch history, which would overwrite the live stream state.
    if (isFileOpen && askAiSseChatId === chatIdFromUrl) {
      const chatKey = String(chatIdFromUrl);
      didFetchChatMessagesRef.current = chatKey;
      return;
    }

    const chatKey = String(chatIdFromUrl);
    if (didFetchChatMessagesRef.current === chatKey) return;
    didFetchChatMessagesRef.current = chatKey;

    dispatch(fetchAskAiSseChatMessages({ userId, caseId, chatId: chatKey })).catch((error: any) => {
      const msg = error?.detail || error?.message || 'Chat not found';
      addToast(msg, { appearance: 'error', autoDismiss: true });

      const search = history.location.search || '';
      if (fileSidebarAskAI) {
        const pathname = history.location.pathname || '';
        const parts = pathname.split('/').filter(Boolean);
        const filesIndex = parts.indexOf('files');
        if (filesIndex !== -1 && parts.length >= filesIndex + 2) {
          const basePath = `/${parts.slice(0, filesIndex + 2).join('/')}`;
          history.push(`${basePath}${search}`);
          return;
        }
      }

      // Redirect to base ai-tools if chat is missing/invalid
      const base = history.location.pathname.replace(/\/ai-tools.*/, '');
      history.push(`${base}/ai-tools${search}`);
    });
  }, [
    dispatch,
    userId,
    chatIdFromUrl,
    addToast,
    isFileOpen,
    askAiSseChatId,
    caseId,
    fileSidebarAskAI,
    skipHydration,
  ]);

  const resetHydrationGuard = useCallback(() => {
    didFetchChatMessagesRef.current = null;
  }, []);

  return { resetHydrationGuard };
}
